import { useState, useRef, useEffect } from 'react';
import { CalendarBlank, ArrowLeft, Warning, ChatText, PaperPlaneTilt, XCircle } from '@phosphor-icons/react';
import ModalCloseButton from '../ui/ModalCloseButton';
import TimePicker from '../ui/TimePicker';
import './DragConfirmModal.css';

/**
 * Props (multi-clinic v2 additions):
 *   absence           — blocking absence at the target slot (null = none)
 *   canForceConflicts — clinic policy lets this user waive a double-booking
 *   canForceAbsence   — clinic policy lets this user waive an absence
 */
function DragConfirmModal({
  appointment,
  newStartTime,
  newEndTime,
  onConfirm,
  onCancel,
  isLoading,
  conflicts = [],
  absence = null,
  canForceConflicts = true,
  canForceAbsence = true,
}) {
  const oldStart = new Date(appointment.startTime);
  const oldEnd = new Date(appointment.endTime);
  const durationMs = oldEnd.getTime() - oldStart.getTime();

  const initialHHmm = `${String(newStartTime.getHours()).padStart(2, '0')}:${String(newStartTime.getMinutes()).padStart(2, '0')}`;
  const [editedTime, setEditedTime] = useState(initialHHmm);
  const [isEditingTime, setIsEditingTime] = useState(false);
  const timePickerRef = useRef(null);

  useEffect(() => {
    if (isEditingTime && timePickerRef.current) {
      const input = timePickerRef.current.querySelector('input');
      if (input) {
        input.focus();
        input.select?.();
      }
    }
  }, [isEditingTime]);

  // Blocked time (חסימת זמן) — no patient, no SMS, no postpone-week semantics.
  const isBlockedTime = appointment.isBlockedTime === true;
  const hasSmsConfigured =
    !isBlockedTime &&
    (appointment.initialSmsEnabled === true || appointment.reminderSmsEnabled === true);
  const [smsOverride, setSmsOverride] = useState(null);
  const willSendSms = smsOverride !== null ? smsOverride : hasSmsConfigured;

  const isInvalidFormat = !/^\d{2}:\d{2}$/.test(editedTime);

  const effectiveStart = new Date(newStartTime);
  if (!isInvalidFormat) {
    const [h, m] = editedTime.split(':').map(Number);
    effectiveStart.setHours(h, m, 0, 0);
  }
  const effectiveEnd = new Date(effectiveStart.getTime() + durationMs);

  const isInPast = !isInvalidFormat && effectiveStart.getTime() < Date.now();
  const isSameTime = !isInvalidFormat && oldStart.getTime() === effectiveStart.getTime();

  const formatDate = (d) => d.toLocaleDateString('he-IL', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const formatTime = (d) => d.toLocaleTimeString('he-IL', {
    hour: '2-digit', minute: '2-digit',
  });

  // Series scope — same three answers as the appointment modal. "Push the rest
  // by a week" only appears once the meeting actually moved to another day.
  const [scope, setScope] = useState('single');
  const dateChanged = !isInvalidFormat && effectiveStart.toDateString() !== oldStart.toDateString();

  useEffect(() => {
    if (!dateChanged && scope === 'postpone_week') setScope('single');
  }, [dateChanged, scope]);

  const HEBREW_DAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
  const dayAndTime = (d) => `יום ${HEBREW_DAYS[d.getDay()]} בשעה ${formatTime(d)}`;

  const handleConfirm = (allowConflicts = false) => {
    onConfirm({
      sendSms: willSendSms,
      startTime: effectiveStart,
      endTime: effectiveEnd,
      scope: appointment.isRecurring ? scope : 'single',
      allowConflicts,
    });
  };

  return (
    <div className="drag-confirm-overlay" onClick={onCancel}>
      <div className="drag-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="drag-confirm-header">
          <CalendarBlank size={24} weight="duotone" />
          <h3>{isBlockedTime ? "שינוי מועד חסימה" : "שינוי מועד פגישה"}</h3>
          <ModalCloseButton onClick={onCancel} />
        </div>

        {/* Scrollable body — the header and the confirm/cancel row stay pinned,
            so on short viewports (iPhone) the buttons are always reachable. */}
        <div className="drag-confirm-body">
        <div className="drag-confirm-patient">{appointment.patientName}</div>

        <div className="drag-confirm-times">
          <div className="time-block old-time">
            <span className="time-block-label">מועד נוכחי</span>
            <span className="time-block-date">{formatDate(oldStart)}</span>
            <span className="time-block-hours">{formatTime(oldStart)} - {formatTime(oldEnd)}</span>
          </div>

          <div className="time-arrow">
            <ArrowLeft size={20} weight="bold" />
          </div>

          <div className="time-block new-time">
            <span className="time-block-label">מועד חדש</span>
            <span className="time-block-date">{formatDate(effectiveStart)}</span>
            {isEditingTime ? (
              <div className="time-block-edit" ref={timePickerRef}>
                <TimePicker
                  value={editedTime}
                  onChange={(e) => setEditedTime(e.target.value)}
                  name="newStartTime"
                  error={isInPast || isInvalidFormat}
                  className="time-block-picker"
                />
                <span className="time-block-end">- {formatTime(effectiveEnd)}</span>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  className={`time-block-hours time-block-hours-editable ${isInPast ? 'has-error' : ''}`}
                  onClick={() => setIsEditingTime(true)}
                  title="לחץ לשינוי השעה"
                >
                  {formatTime(effectiveStart)} - {formatTime(effectiveEnd)}
                </button>
                <span className="time-block-hint">לשינוי ידני של השעה — לחץ עליה</span>
              </>
            )}
          </div>
        </div>

        {isInPast && (
          <div className="drag-confirm-error">
            <Warning size={18} weight="fill" />
            <span>לא ניתן לשנות לזמן שכבר עבר</span>
          </div>
        )}

        {appointment.isRecurring && (
          <div className="drag-confirm-scope">
            <span className="drag-scope-title">הפגישה היא חלק מסדרה — מה לעדכן?</span>
            <label className="drag-scope-option">
              <input
                type="radio"
                name="dragScope"
                value="single"
                checked={scope === 'single'}
                onChange={(e) => setScope(e.target.value)}
                disabled={isLoading}
              />
              <span>
                פגישה זו בלבד
                <small>
                  רק פגישה זו תעבור ל{formatDate(effectiveStart)} בשעה{' '}
                  {formatTime(effectiveStart)} — שאר הסדרה תישאר כפי שהיא
                </small>
              </span>
            </label>
            <label className="drag-scope-option">
              <input
                type="radio"
                name="dragScope"
                value="future"
                checked={scope === 'future'}
                onChange={(e) => setScope(e.target.value)}
                disabled={isLoading}
              />
              <span>
                פגישה זו וכל הפגישות הבאות
                <small>כל הפגישות הבאות יעברו ל{dayAndTime(effectiveStart)}</small>
              </span>
            </label>
            {dateChanged && !isBlockedTime && (
              <label className="drag-scope-option">
                <input
                  type="radio"
                  name="dragScope"
                  value="postpone_week"
                  checked={scope === 'postpone_week'}
                  onChange={(e) => setScope(e.target.value)}
                  disabled={isLoading}
                />
                <span>
                  פגישה זו בלבד, ולדחות את שאר הפגישות בשבוע
                  <small>
                    פגישה זו תעבור ל{formatDate(effectiveStart)} בשעה{' '}
                    {formatTime(effectiveStart)}; הפגישות הבאות יידחו בשבוע
                    {/* A twice-weekly series has two different slots, so naming
                        one day would be wrong for half the meetings. */}
                    {appointment.recurringFrequency === 'twice_weekly'
                      ? ' — כל אחת נשארת ביום ובשעה שלה'
                      : ` — יישארו ב${dayAndTime(oldStart)}`}
                  </small>
                </span>
              </label>
            )}
          </div>
        )}

        {conflicts.length > 0 && (
          <div className="drag-confirm-error drag-confirm-conflicts">
            <Warning size={18} weight="fill" />
            <div>
              <span>{`נמצאו ${conflicts.length} התנגשויות עם פגישות קיימות`}</span>
              <ul>
                {conflicts.slice(0, 3).map((conflict, idx) => (
                  <li key={idx}>
                    {formatDate(new Date(conflict.date))} בשעה{' '}
                    {formatTime(new Date(conflict.date))}
                    {conflict.existingAppointment?.patientName
                      ? ` — ${conflict.existingAppointment.patientName}`
                      : ''}
                  </li>
                ))}
                {conflicts.length > 3 && <li>{`ועוד ${conflicts.length - 3}...`}</li>}
              </ul>
              {canForceConflicts ? (
                <button
                  type="button"
                  className="drag-conflicts-force"
                  onClick={() => handleConfirm(true)}
                  disabled={isLoading}
                >
                  עדכן בכל זאת
                </button>
              ) : (
                <span>מדיניות הקליניקה חוסמת כפל תורים למטפל/ת. רק מנהל/ת יכול/ה לעקוף.</span>
              )}
            </div>
          </div>
        )}

        {absence && (
          <div className="drag-confirm-error drag-confirm-conflicts">
            <Warning size={18} weight="fill" />
            <div>
              <span>
                {`המטפל/ת לא זמין/ה במועד זה (היעדרות${
                  absence.allDay === false && absence.startTime ? ` ${absence.startTime}–${absence.endTime}` : ""
                })`}
              </span>
              {canForceAbsence ? (
                <button
                  type="button"
                  className="drag-conflicts-force"
                  onClick={() => handleConfirm(true)}
                  disabled={isLoading}
                >
                  עדכן בכל זאת
                </button>
              ) : (
                <span>מדיניות הקליניקה חוסמת קביעת תורים בזמן היעדרות. רק מנהל/ת יכול/ה לעקוף.</span>
              )}
            </div>
          </div>
        )}

        {!isBlockedTime && (
        <div className={`drag-confirm-sms ${willSendSms ? 'has-sms' : 'no-sms'}`}>
          <ChatText size={18} weight="duotone" />
          <span>
            {willSendSms
              ? 'המטופל יקבל הודעת SMS עבור שינוי הפגישה'
              : 'המטופל לא יקבל הודעת SMS עבור שינוי הפגישה כי לא הוגדרה תזכורת SMS עבור הפגישה הראשית'}
          </span>
        </div>
        )}

        {isBlockedTime ? null : willSendSms ? (
          <button
            className="drag-sms-toggle cancel-sms"
            onClick={() => setSmsOverride(false)}
            disabled={isLoading}
          >
            <XCircle size={16} weight="duotone" />
            <span>בטל שליחת SMS למטופל עבור שינוי זה</span>
          </button>
        ) : (
          <button
            className="drag-sms-toggle send-sms"
            onClick={() => setSmsOverride(true)}
            disabled={isLoading}
          >
            <PaperPlaneTilt size={16} weight="duotone" />
            <span>שלח הודעת SMS למטופל עבור שינוי זה</span>
          </button>
        )}
        </div>

        <div className="drag-confirm-actions">
          <button
            className="drag-confirm-btn confirm"
            onClick={() => handleConfirm(false)}
            disabled={isLoading || isSameTime || isInPast || isInvalidFormat}
          >
            {isLoading ? 'מעדכן...' : 'אשר שינוי'}
          </button>
          <button
            className="drag-confirm-btn cancel"
            onClick={onCancel}
            disabled={isLoading}
          >
            ביטול
          </button>
        </div>
      </div>
    </div>
  );
}

export default DragConfirmModal;
